import dayjs from "dayjs";
import { AccountStructure } from "../models/accounts-structure";
import { DocumentStructure } from "../models/document-structure";

export const deleteEmptyFieldsFromObject = <T>(obj: T): T => {
    // Create a shallow copy of the object
    const objCopy = { ...obj };

    for (const key in objCopy) {
        if (Object.prototype.hasOwnProperty.call(objCopy, key)) {
            const value = objCopy[key];

            if (
                value == null ||
                value === -1 ||
                (typeof value === 'string' && value.trim() === '')
            ) {
                delete objCopy[key];
            } else if (Array.isArray(value)) {
                const cleanedArray = value
                    .map((item) => (typeof item === 'object' && item !== null ? deleteEmptyFieldsFromObject(item) : item))
                    .filter((item) => {
                        if (typeof item === 'object' && item !== null) {
                            return Object.keys(item).length > 0;
                        }
                        return item != null && item !== -1 && (typeof item !== 'string' || item.trim() !== '');
                    });

                if (cleanedArray.length > 0) {
                    (objCopy as any)[key] = cleanedArray;
                } else {
                    delete objCopy[key];
                }
            } else if (typeof value === 'object' && value !== null) {
                const cleanedObject = deleteEmptyFieldsFromObject(value);

                if (Object.keys(cleanedObject).length > 0) {
                    (objCopy as any)[key] = cleanedObject;
                } else {
                    delete objCopy[key];
                }
            }
        }
    }

    return objCopy;
};


export const groupBy = <T>(array: T[] | undefined, property: keyof T): Record<string | number , T[]> => {
    return array?.reduce((acc, obj) => {
        // Get the value of the property
        const key = obj[property] as unknown as string | number;
        // If the key doesn't exist in the accumulator, create an empty array for it
        if (!acc[key]) {
            acc[key] = [];
        }
        // Push the object into the corresponding group array
        acc[key].push(obj);
        return acc;
    }, {} as Record<string | number, T[]>)?? {};
}

export const dateStringFormatPrinter = (dateString: string | null, format?: string): string => {

    if(dateString === null){
        return "";
    }

    return dayjs(dateString).format(format || "DD/MM/YYYY");
}

// date object to string, format DD/MM/YYYY
export const dateToString = (date: Date | null) => {

    if(!date){
        return ''
    }

    // Extract day, month, and year from the date object
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Month is zero-based, so we add 1
    const year = date.getFullYear();

    // Return the formatted date string
    return `${day}/${month}/${year}`;
}


export const compareObjects = <T>(obj1: T, obj2: T): boolean => {
    if (obj1 === obj2) {
        return true;
    }

    if (typeof obj1 !== 'object' || obj1 === null || typeof obj2 !== 'object' || obj2 === null) {
        return false;
    }

    const keys1 = Object.keys(obj1) as Array<keyof T>;
    const keys2 = Object.keys(obj2) as Array<keyof T>;

    if (keys1.length !== keys2.length) {
        return false;
    }

    for (const key of keys1) {
        if (!keys2.includes(key)) {
            return false;
        }

        const val1 = obj1[key];
        const val2 = obj2[key];

        const areObjects = typeof val1 === 'object' && val1 !== null && typeof val2 === 'object' && val2 !== null;
        if (
            (areObjects && !compareObjects(val1, val2)) ||
            (!areObjects && val1 !== val2)
        ) {
            return false;
        }
    }

    return true;
}

export const fixedNumber = (number: number, digits: number = 2): number => {

    return Number(number.toFixed(digits));
}


export const setAllValuesToNull = <T>(obj: T): T => {
    if (typeof obj !== 'object' || obj === null) {
        return obj;
    }

    if (Array.isArray(obj)) {
        return obj.map(item => setAllValuesToNull(item)) as unknown as T;
    }

    const newObj: any = {};
    for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
            const value = (obj as any)[key];
            if (typeof value === 'object' && value !== null) {
                newObj[key] = setAllValuesToNull(value);
            } else {
                newObj[key] = null;
            }
        }
    }

    return newObj;
}


export const padWithZeros = (num: number | undefined, width: number): string => {

    if(!num){
        return '';
    }

    const numStr = num.toString();
    return numStr.padStart(width, '0');
}


export const checkInputValue = (value: string | number | boolean | null | undefined, expected: string = 'string'): string | number | null =>{

    

    if (value === null || value === undefined) {

        // if (expected === 'number') {
        //     return null;
        // }

        return "";
    }

    if(expected === 'index' && value === 0){
        return "";
    }

    if (typeof value === 'boolean') {
        return value ? 1 : 0;
    }

    if (typeof value === 'string' && expected === 'number') {
        const num = parseFloat(value);
        return isNaN(num) ? value : num;
    }


    return value;

}

export const getValueByPath = <T>(obj: Record<string, any>, path: string): T | undefined =>{
    // Split the path into keys
    const keys = path.split('.');
  
    // Traverse the object using the keys
    return keys.reduce((acc: any, key: string) => {
      // Check if the current level exists and continue traversal
      return acc && acc[key] !== undefined ? acc[key] : undefined;
    }, obj) as T;
  }


/**
 * Parses a base-10 unsigned integer string (ASCII digits only) into a number.
 * - Rejects whitespace, signs, decimals, and non-digits (e.g. " 42", "-1", "2a", "3.14")
 * - Throws TypeError for invalid format and RangeError for non-safe integers
 */

export function parseUnsignedIntStrict(str: string): number {
  const s = str.trim();
  if (!/^\d+$/.test(s)) {
    throw new TypeError(`Invalid unsigned integer string: "${str}"`);
  }
  const n = Number(s);
  if (!Number.isSafeInteger(n)) {
    throw new RangeError(`Not a safe integer: ${s}`);
  }
  return n;
}


/**
 * If it is a Document Field, get it's prefix. otherwise return undefined.
 * @param field 
 */
export function getDocumentFieldPrefix(field: AccountStructure | DocumentStructure): string | undefined {
    
    switch ((field as DocumentStructure).type) {
        case "חשבון":
            return "document_account";
    
        case "רכב":
            return "car";

        case "פריט":
            return "items";

        case "עבודה":
            return "works";

        default:
            break;
    }        
}